import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Typography
} from '@material-ui/core';
import { SketchPicker } from 'react-color';
import { getLocalStorageAuthentication } from '../../lib/auth';
import { BASE_END_POINT } from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 800,
    margin: '0 auto'
  },
  card: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 700,
    textAlign: 'center'
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    textAlign: 'center'
  },
  colorPickerSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(4)
  },
  colorPickerLabel: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    fontSize: '1rem'
  },
  colorPreview: {
    width: 100,
    height: 100,
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    boxShadow: theme.shadows[2]
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4)
  },
  applyButton: {
    minWidth: 120,
    padding: theme.spacing(1.5, 4),
    fontWeight: 600
  },
  cancelButton: {
    minWidth: 120,
    padding: theme.spacing(1.5, 4)
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh'
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(4)
  }
}));

const PlanConfigurationView = () => {
  const classes = useStyles();
  const { jobId } = useParams();
  const history = useHistory();
  const [brandColor, setBrandColor] = useState('#FF6B6B');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch existing plan for this job
  useEffect(() => {
    const fetchBrandColor = async () => {
      if (!jobId) return;

      try {
        setLoading(true);
        const localAuth = getLocalStorageAuthentication();

        console.log('🔄 [PlanConfiguration] Fetching brand color for job:', jobId);

        const response = await fetch(
          `${BASE_END_POINT}/api/v1/benefit-duration/job/${jobId}/brand-color`,
          {
            headers: {
              Authorization: `Bearer ${localAuth.token}`
            }
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log('✅ [PlanConfiguration] Brand color response:', result);

          if (result.data && result.data.brandColor) {
            setBrandColor(result.data.brandColor);
          } else {
            console.warn('⚠️ [PlanConfiguration] No brand color benefit found');
            setError('No brand color benefit found for this job.');
          }
        } else {
          console.warn('⚠️ [PlanConfiguration] Failed to fetch brand color');
          setError('Failed to load brand color. Please try again later.');
        }
      } catch (err) {
        console.error('❌ [PlanConfiguration] Error fetching brand color:', err);
        setError('Failed to load brand color configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchBrandColor();
  }, [jobId]);

  const handleColorChange = (color) => {
    setBrandColor(color.hex);
  };

  const handleApply = async () => {
    try {
      setSaving(true);
      const localAuth = getLocalStorageAuthentication();

      console.log('💾 [PlanConfiguration] Saving brand color:', brandColor);

      // Update active brand color benefit metadata
      const response = await fetch(
        `${BASE_END_POINT}/api/v1/benefit-duration/job/${jobId}/brand-color`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localAuth.token}`
          },
          body: JSON.stringify({
            brandColor: brandColor
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update plan');
      }

      console.log('✅ [PlanConfiguration] Plan updated successfully');

      // Go back to dashboard
      history.goBack();
    } catch (err) {
      console.error('❌ [PlanConfiguration] Error saving:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  // Loading state
  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Loading configuration...
          </Typography>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={classes.root}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.errorContainer}>
              <Typography variant="h6" color="error" gutterBottom>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={handleCancel}
                style={{ marginTop: 16 }}>
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Paper>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h4" className={classes.title}>
              Edit Brand Color
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
              Update the highlight color used for this job.
            </Typography>

            <div className={classes.colorPickerSection}>
              <Typography className={classes.colorPickerLabel}>
                Select Your Brand Color
              </Typography>

              <SketchPicker
                color={brandColor}
                onChangeComplete={handleColorChange}
              />

              <Paper
                className={classes.colorPreview}
                style={{ backgroundColor: brandColor }}
                elevation={3}
              />

              <Typography variant="caption" style={{ marginTop: 8 }}>
                Preview: {brandColor}
              </Typography>
            </div>

            <div className={classes.buttonGroup}>
              <Button
                variant="outlined"
                color="default"
                className={classes.cancelButton}
                onClick={handleCancel}
                disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.applyButton}
                onClick={handleApply}
                disabled={saving}>
                {saving ? <CircularProgress size={24} /> : 'Apply'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </Paper>
    </div>
  );
};

export default PlanConfigurationView;
